<script setup lang="ts">
import { Button } from "@/components/ui/button";
import { AutoForm } from "@/ui-plus/auto-form";
import { toTypedSchema } from "@vee-validate/zod";
import { useForm } from "vee-validate";
import { z } from "zod";
import { loginPhone } from "@auth-frontend-vue/lib/auth.actions";
import { useFormPlus } from "@/ui-plus/form/useFormPlus";
import { Loader2 } from "lucide-vue-next";

const props = defineProps<{
	phoneNumber: string;
	/** autofilled password captured on identifier step */
	password?: string;
}>();

const emit = defineEmits<{
	forgotPassword: [];
}>();

const schema = z.object({
	password: z.string().min(8).max(255)
});
const form = useForm({
	validationSchema: toTypedSchema(schema),
	initialValues: {
		password: props.password ?? '',
	},
});
const { isLoading, globalError, handleError, withSubmit } = useFormPlus(form);

async function onSubmit(values: z.infer<typeof schema>) {
	await withSubmit(async () => {
		const { error } = await loginPhone({
			phoneNumber: props.phoneNumber,
			password: values.password,
		});
		if (error) {
			handleError(error, form);
		}
	})
}
</script>

<template>
	<div>
		<h1 class="text-2xl font-bold">{{ $t('welcomeBack') }}</h1>
		<p class="text-sm text-muted-foreground mt-1">{{ $t('enterPasswordToContinue') }}</p>

		<!-- password managers need username in the same form as current-password (not type=hidden) -->
		<input type="tel"
			   :value="phoneNumber"
			   name="username"
			   autocomplete="username"
			   readonly
			   tabindex="-1"
			   aria-hidden="true"
			   class="sr-only" />

		<AutoForm class="space-y-6 mt-6"
				  :schema="schema"
				  :form="form"
				  @submit="onSubmit"
				  :field-config="{
					password: {
						inputProps: {
							type: 'password',
							autocomplete: 'current-password',
							autofocus: true,
						}
					}
				}">
			<div class="text-sm font-medium text-destructive"
				 v-if="globalError">
				{{ globalError }}
			</div>
			<div>
				<Button type="submit"
						:disabled="isLoading"
						class="w-full my-6">
					<Loader2 v-if="isLoading"
							 class="animate-spin" />
					{{ $t('login') }}
				</Button>
			</div>
		</AutoForm>
		<div class="text-center">
			<button type="button"
					@click="emit('forgotPassword')"
					class="inline-block text-sm underline">
				{{ $t('forgotPassword') }}
			</button>
		</div>
	</div>
</template>
